import {
  AllFlowsDefinition,
  FlowAction,
  FlowDefinition,
  FunnelState,
  FormAnswer,
  MoveFlowAction,
  PaymentDetailsData,
  ReviewDetailsData,
} from 'scenes/common/types';
import { LandlordFormValues } from 'scenes/landlord/hooks/useActions/types';
import { LandlordApiResponses } from 'scenes/landlord/actions/types';

import {
  LandlordQuoteData,
  LandlordQuoteSubSection,
} from './quoteDetails/types';
import {
  LandlordPolicySubSection,
  PolicyDetailsData,
} from './policyDetails/types';
import {
  CoverOptionsData,
  LandlordCoverSubSection,
} from './coverOptions/types';

export enum LandlordFlowType {
  QuoteDetails = 'quoteDetails',
  CoverOptions = 'coverOptions',
  PolicyDetails = 'policyDetails',
  PaymentDetails = 'paymentDetails',
  ReviewDetails = 'reviewDetails',
}

export enum LandlordStepType {
  CoverTypeSelection = 'cover-type-selection',
  PolicyStartDate = 'policy-start-date',
  MainAddress = 'main-address',
  Ownership = 'ownership',
  PropertyPurpose = 'property-purpose',
  KickOutChoice = 'kick-out-choice',
  RentalAgreement = 'rental-agreement',
  WeeklyRentPaid = 'weekly-rent-paid',
  PropertyUsedForBusiness = 'property-used-for-business',
  PersonalOrBusinessPolicy = 'personal-business-policy',
  ContactDateOfBirth = 'contact-date-of-birth',
  RecentInsurer = 'recent-insurer',
  YearsInsured = 'years-insured',
  ClaimsMade = 'claims-made',
  BreakInAndAdditionalSecurity = 'break-in-and-additional-security',
  BuildingType = 'building-type',
  NumberOfBedrooms = 'number-of-bedrooms',
  PropertyBuiltYear = 'property-built-year',
  RoofMaterial = 'roof-material',
  WallMaterial = 'wall-material',
  SecurityFeatures = 'security-features',
  SumInsuredChoice = 'estimated-sum-insured-choice',
  SumInsuredBuildingValue = 'building-sum-insured',
  SumInsuredContentsValue = 'contents-sum-insured',
  FlybuysNumber = 'flybuys-number',
  PromoCode = 'promo-code',
  QuotePrice = 'quote-price',
  BuildingExcess = 'building-excess',
  ContentsExcess = 'contents-excess',
  OptionalExtras = 'optional-extras',
  PropertyCondition = 'property-condition',
  HeritageListed = 'heritage-listed',
  RewiredOrReplumbed = 'rewired-or-replumbed',
  PropertyUnoccupied = 'property-unoccupied',
  PropertyRenovation = 'property-renovation',
  Over5Acres = 'over-five-acres',
  PropertyOccupancy = 'property-occupancy',
  MortgageProvider = 'mortgage-provider',
  PersonalPolicyContact = 'personal-policy-contact',
  BusinessNameAndAbn = 'business-name-and-abn',
  BusinessContact = 'business-contact',
  ContactEmailAddress = 'contact-email-address',
  ContactPhoneNumber = 'contact-phone-number',
  AddAdditionalPolicyHolder = 'add-additional-policy-holder',
  AdditionalPolicyHolder = 'additional-policy-holder',
  AdditionalPolicyHolderList = 'additional-policy-holder-list',
  PostalAddress = 'postal-address',
  DifferentPostalAddress = 'different-postal-address',
  DifferentPostalAddressManual = 'different-postal-address-manual',
  LockedBagAddress = 'locked-bag-address',
  PolicyHoldersHistory = 'policy-holders-history',
  MarketingConsent = 'marketing-consent',
  TermsAndConditions = 'terms-and-conditions',
  Summary = 'summary',
  PaymentFrequency = 'payment-frequency',
  PaymentDate = 'payment-date',
  PaymentMethod = 'payment-method',
  DirectDebitPayment = 'direct-debit-payment',
  CreditCardPayment = 'credit-card-payment',
  PaymentConfirmation = 'payment-confirmation',
  ReviewPolicyStartDate = 'review-policy-start-date',
}

export enum LandlordMoveFlows {
  EditQuoteDetails = 'editQuoteDetails',
  EditPolicyDetails = 'editPolicyDetails',
  EditCoverDetails = 'editCoverDetails',
}

export type LandlordFunnelState = FunnelState<
  LandlordFlowType,
  LandlordStepType
> & {
  [LandlordFlowType.QuoteDetails]: LandlordQuoteData;
  [LandlordFlowType.CoverOptions]: CoverOptionsData;
  [LandlordFlowType.PolicyDetails]: PolicyDetailsData;
  [LandlordFlowType.PaymentDetails]: PaymentDetailsData;
  [LandlordFlowType.ReviewDetails]: ReviewDetailsData;
  apiResponses: LandlordApiResponses;
};

export type LandlordFlowDefinition = FlowDefinition<
  LandlordFormValues,
  LandlordFlowType,
  LandlordStepType,
  LandlordFunnelState
>;
export type LandlordAllFlowDefinition = AllFlowsDefinition<
  LandlordFormValues,
  LandlordFlowType,
  LandlordStepType,
  LandlordFunnelState
>;
export type LandlordFlowAction = FlowAction<
  LandlordFormValues,
  LandlordStepType
>;
export type LandlordFormAnswer = FormAnswer<
  LandlordFormValues,
  LandlordStepType
>;
export type LandlordMoveFlowAction = MoveFlowAction<
  LandlordFormValues,
  LandlordMoveFlows,
  LandlordStepType
>;

export type LandlordSubSection =
  | LandlordQuoteSubSection
  | LandlordPolicySubSection
  | LandlordCoverSubSection;

export enum LandlordSection {
  QuoteDetails = 'Quote details',
  CoverDetails = 'Cover details',
  PolicyDetails = 'Policy details',
}
